import React, { useEffect, useMemo, useState } from 'react';

import { IAbstractRecord } from '@models';
import { isEmpty } from '@utils/validationUtils';

import {
  clearAllInvalidDatePickerFields,
  hasInvalidDatePickerFields,
  mergeInvalidDatePickerErrors
} from '../form-fields/datePickerValidation';
import EnhancedButton from '../button/EnhancedButton';
import { DynamicFormWidgetInputType, FormFieldType } from './types/inputTypes';
import DynamicFormSection from './DynamicFormSection';
import { useDynamicFormDrawerStyles } from './style';
import { IDynamicFormWidget } from './types';
import { validateDynamicFormWidget } from './utils';

import clsx from 'clsx';
import { cloneDeep } from 'lodash';

const DynamicFormWidget: React.FC<IDynamicFormWidget> = ({
  sections,
  buttonText,
  loadingFields = {},
  disableForm = false,
  submitButtonState = null,
  isSubmitButtonDisabled = false,
  forceDisable = true,
  error,
  noteSection,
  onSubmit,
  onChange,
  onCustomBlur,
  onCustomValidate,
  onAutocompleteSearch,
  initialMultiAutocompleteSearch
}) => {
  const [values, setValues] = useState<IAbstractRecord>();
  const [errors, setErrors] = useState<IAbstractRecord>(error || {});
  const [touched, setTouched] = useState<IAbstractRecord>();
  const [validatingForm, setValidatingForm] = useState<boolean>();
  const { classes } = useDynamicFormDrawerStyles();

  const inputs: Record<string, DynamicFormWidgetInputType> = useMemo(() => {
    const tempInputs: Record<string, DynamicFormWidgetInputType> = {};
    sections.forEach((section) => {
      Object.values(section.inputs).forEach((input) => {
        tempInputs[input.name] = input;
      });
    });

    return tempInputs;
  }, [sections]);

  useEffect(() => {
    const nextValues = cloneDeep(values) || {};
    const nextErrors = cloneDeep(errors) || {};
    const nextTouched = cloneDeep(touched) || {};

    if (!values) {
      Object.keys(inputs).forEach((name) => {
        nextValues[name] = inputs[name].value;
        nextErrors[name] = inputs[name].error || '';
        nextTouched[name] = !!inputs[name].error;
      });
    } else {
      Object.keys(inputs).forEach((name) => {
        nextValues[name] = inputs[name].value;
      });
      setValues(nextValues);

      return;
    }

    setValues(nextValues);
    setErrors(nextErrors);
    setTouched(nextTouched);
  }, [sections]);

  useEffect(() => {
    if (error) setErrors(mergeInvalidDatePickerErrors(error));
  }, [error]);

  useEffect(() => {
    clearAllInvalidDatePickerFields();

    return () => {
      clearAllInvalidDatePickerFields();
    };
  }, []);

  const onFieldBlur = async (
    name: string,
    additionalProperties?: {
      subFieldName?: string;
      index?: number;
      input?: DynamicFormWidgetInputType;
    }
  ) => {
    const { subFieldName, index, input } = additionalProperties || {};
    const touchedTemp = { ...touched };

    if (input?.type === FormFieldType.repeaterBox) {
      if (!Array.isArray(touchedTemp[name])) {
        touchedTemp[name] = [];
      }
      touchedTemp[name][index] = touchedTemp[name][index] || {};
      touchedTemp[name][index][subFieldName] = true;
      setErrors((prev) => ({
        ...prev,
        [name]: validateDynamicFormWidget(values[name], input, values)
      }));
    } else {
      touchedTemp[name] = true;
    }

    if (onCustomBlur) {
      setValidatingForm(true);
      const result = await onCustomBlur(
        name,
        values,
        errors,
        subFieldName,
        index
      );
      setValidatingForm(false);
      setValues(result.values);
      setErrors(result.errors);
      if (result.touched) {
        Object.entries(result.touched).forEach(([key, value]) => {
          touchedTemp[key] = value;
        });
      }
    }

    setTouched(touchedTemp);
  };

  const onFieldChange = (
    name: string,
    value: any,
    sectionIndex?: number,
    additionalProperties?: {
      subFieldName?: string;
      index?: number;
      input?: DynamicFormWidgetInputType;
    }
  ) => {
    const { input } = additionalProperties || {};
    const valuesTemp = { ...values, [name]: value };
    const errorsTemp = { ...errors };
    const touchedTemp = { ...touched };

    const isRepeater = input?.type === FormFieldType.repeaterBox;

    if (isRepeater && !Array.isArray(touchedTemp[name])) {
      touchedTemp[name] = [];
    }

    if (isRepeater && input?.inputs) {
      const prevLength = Array.isArray(values[name]) ? values[name].length : 0;
      const newLength = Array.isArray(value) ? value.length : 0;

      value.forEach((_: any, rowIdx: number) => {
        touchedTemp[name][rowIdx] = touchedTemp[name][rowIdx] || {};
        Object.keys(input.inputs).forEach((key) => {
          touchedTemp[name][rowIdx][key] =
            touchedTemp[name][rowIdx][key] ||
            (newLength < prevLength ? true : false);
        });
      });

      if (newLength < prevLength) {
        errorsTemp[name] = (errorsTemp[name] || []).slice(0, newLength);
      }

      errorsTemp[name] = validateDynamicFormWidget(
        valuesTemp[name],
        input,
        valuesTemp
      );
    } else {
      touchedTemp[name] = true;
      errorsTemp[name] = validateDynamicFormWidget(
        value,
        inputs[name],
        valuesTemp
      );
      Object.keys(inputs).forEach((otherName) => {
        if (otherName !== name && inputs[otherName].customValidator) {
          errorsTemp[otherName] = validateDynamicFormWidget(
            valuesTemp[otherName],
            inputs[otherName],
            valuesTemp
          );
        }
      });
    }

    const mergedErrors = mergeInvalidDatePickerErrors(errorsTemp);

    onChange?.(
      name,
      value,
      sectionIndex,
      valuesTemp,
      mergedErrors,
      touchedTemp,
      additionalProperties
    );
    setValues(valuesTemp);
    setErrors(mergedErrors);
    setTouched(touchedTemp);
  };
  const validate = async (): Promise<IAbstractRecord> => {
    setValidatingForm(true);
    let newErrors: IAbstractRecord = {};
    Object.values(inputs).forEach((input) => {
      newErrors[input.name] = validateDynamicFormWidget(
        values[input.name],
        input,
        values
      );
    });

    if (onCustomValidate) {
      newErrors = await onCustomValidate(values, newErrors);
    }

    newErrors = mergeInvalidDatePickerErrors(newErrors);

    setValidatingForm(false);

    return newErrors;
  };

  const handleSubmit = async () => {
    if (!disableForm && !validatingForm) {
      const validateErrors = await validate();
      const hasInvalidDatePickers = hasInvalidDatePickerFields();
      const isInValid =
        hasInvalidDatePickers ||
        Object.values(validateErrors).some((v) => {
          if (Array.isArray(v)) {
            return v.some((e) => Object.values(e).some((i) => !isEmpty(i)));
          }

          return !isEmpty(v);
        });

      if (isInValid) {
        const initialTouched: Record<string, boolean> = {};
        Object.values(inputs).forEach((input) => {
          initialTouched[input.name] = true;
        });
        setErrors(validateErrors);
        setTouched(initialTouched);
      } else {
        onSubmit(values);
      }
    }
  };

  const hasValidationErrors = useMemo(() => {
    if (!errors) return false;

    return Object.values(errors).some((v) => {
      if (Array.isArray(v)) {
        return v.some((e) => Object.values(e).some((i) => !isEmpty(i)));
      }

      return !isEmpty(v);
    });
  }, [errors]);

  const shouldDisableSubmit =
    (forceDisable ?? true) &&
    (disableForm || isSubmitButtonDisabled || hasValidationErrors);

  return values ? (
    <form
      className={classes.drawerContent}
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <div>
        {sections.map((section, sectionIndex: number) => (
          <DynamicFormSection
            key={section.sectionTitle}
            sectionTitle={section.sectionTitle}
            inputs={section.inputs}
            hidden={section.hidden}
            sectionIndex={sectionIndex}
            onFieldChange={onFieldChange}
            onFieldBlur={onFieldBlur}
            disableForm={disableForm}
            loadingFields={loadingFields}
            onAutocompleteSearch={onAutocompleteSearch}
            initialMultiAutocompleteSearch={initialMultiAutocompleteSearch}
            values={values}
            errors={errors}
            touched={touched}
          />
        ))}
      </div>

      <div>
        {noteSection && (
          <p className={clsx(classes.note, noteSection.className)}>
            {noteSection.title && <b>{`${noteSection.title}`}</b>}
            {noteSection.text}
          </p>
        )}

        <div className={classes.submitButtonContainer}>
          <EnhancedButton
            type="submit"
            disabled={shouldDisableSubmit}
            isPrimary
            state={submitButtonState}
          >
            {buttonText || 'Submit'}
          </EnhancedButton>
        </div>
      </div>
    </form>
  ) : (
    <></>
  );
};

export default DynamicFormWidget;
